import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import styles from "./Reminders.module.scss";
import RoundedTabs from "../../../components/roundedTabs/RoundedTabs";
import RemindersHeader from "../../../components/remindersDrawer/RemindersHeader";
import RemindersCard from "../../../components/remindersDrawer/RemindersCard";
import { getReminders } from "../../../store/actions/general.actions";
import { reminders as remindersList, createdReminders as createdRemindersList } from "../../../store/selectors/general.selector";
import SelfCreated from "../../../components/remindersDrawer/SelfCreated";
import NotificationModals from "../../../components/notificationsDrawer/NotificationsModals";
import { api as generalService } from '../../../store/services/general.service';
import { REDIRECTION_TYPES } from '../../../helpers/utils';
import { useHistory } from "react-router-dom/cjs/react-router-dom";

const Reminders = () => {
  const dispatch = useDispatch();
  const PUBLIC_URL = process.env.PUBLIC_URL;

  let receivedReminders = useSelector(remindersList);
  let createdReminders = useSelector(createdRemindersList);

  const [allReminders, setReminders] = useState([]);
  const [filteredReminders, setFilteredReminders] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reminder, setReminder] = useState("");
  const [modalType, setModalType] = useState("");
  const [isCreate, setIsCreate] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  const cardRef = useRef(null);
  const history = useHistory();

  const transformLinks = (text, links) => {
      if (!text || text.trim() === "" || !text.includes("<@link:"))
        return text;
  
      const regex = /<@link:(\w+):([a-f0-9\-]+)@>/g;
      const transformedString = text.replace(regex, (match, type, id) => {
        let entityName;
        if (links && links?.length > 0) {
          entityName = links.filter((e) => e.linkId === id).map((e) => e.linkName);
        }
        let linkUrl = "";
        if (type === "Client" || type === "Project" || type === "Scope")
          linkUrl = `${PUBLIC_URL}/${type.toLowerCase()}s/${id}/overview`;
        else if (type === "Portfolio" || type === "Program")
          linkUrl = `${PUBLIC_URL}/${type.toLowerCase()}/${id}/overview`;
        else if (type === "ChangeRequest" || type === "CommunicationPlan" || type === "Budget" || type === "Risk" || type === "Task")
          return `<a target="_blank" class="notification-redirection-link cursor-pointer" data-type="${type.toLowerCase()}" data-id="${id}">${entityName}</a>`;
  
        if (linkUrl !== "")
          return `<a href=${linkUrl} target="_self" data-type="${type}" data-id="${id}">${entityName}</a>`;
        else
          return `<span class="text-primary">${entityName}</span>`;
      });
      return transformedString;
    }
  
    useEffect(() => {
      const handleLinkClick = (e) => {
        const target = e.target;
        // Check if the clicked element has the redirection class
        if (target.classList.contains('notification-redirection-link')) {
          e.preventDefault();
          e.stopPropagation();
  
          const type = target.getAttribute('data-type');
          const id = target.getAttribute('data-id');
  
          if (type && id) {
            generalService.getNotificationLinkRedirectionRequest({ type, id })
              .then((response) => {
                if (response) {
                  if (response.scopeId)
                    handleScopeRedirection(response.scopeId, id);
  
                  else if (response.projectId)
                    handleProjectRedirection(response.projectId, type, id);
  
                }
              }).catch((error) => {
              });
          }
        }
      };
  
      const cardElement = cardRef.current;
      if (cardElement) {
        cardElement.addEventListener('click', handleLinkClick);
      }
  
      return () => {
        if (cardElement) {
          cardElement.removeEventListener('click', handleLinkClick);
        }
      };
    }, []);
    const handleScopeRedirection = (scopeId, taskId) => {
      history.push(`/scopes/${scopeId}/overview?r_id=${taskId}`);
    };
  
    const handleProjectRedirection = (projectId, type, id) => {
      if (projectId && type && id) {
        let redirectionType;
        switch (type) {
          case 'changerequest':
            redirectionType = REDIRECTION_TYPES.CHANGE_REQUEST;
            break;
          case 'communicationplan':
            redirectionType = REDIRECTION_TYPES.COMMUNICATION;
            break;
          case 'budget':
            redirectionType = REDIRECTION_TYPES.BUDGET;
            break;
          case 'risk':
            redirectionType = REDIRECTION_TYPES.RISK;
            break;
          case 'task':
            redirectionType = REDIRECTION_TYPES.TASK;
            break;
          default:
            return;
        }
  
        history.push(`/projects/${projectId}/overview?r_t=${redirectionType}&r_id=${id}`);
      }
  
    };

  const filterReminders = (query, reminders) => {
    if (!query.trim()) return reminders;

    const lowerQuery = query.toLowerCase();
    return reminders.filter((r) => {
      const transformedTitle = transformLinks(r.title, r.links).toLowerCase();
      const transformedNotes = transformLinks(r.notes, r.links).toLowerCase();
      return (
        transformedTitle.includes(lowerQuery) ||
        transformedNotes.includes(lowerQuery)
      );
    });
  };

  const applyFilters = (query, reminders, unreadOnly) => {
    let filtered = filterReminders(query, reminders);

    if (unreadOnly) {
      filtered = filtered.filter((r) => !r.isRead);
    }

    return filtered;
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    const filtered = applyFilters(query, allReminders, showUnreadOnly);
    setFilteredReminders(filtered);
  };

  const handleFilterChange = (unreadOnly) => {
    setShowUnreadOnly(unreadOnly);
    const filtered = applyFilters(searchQuery, allReminders, unreadOnly);
    setFilteredReminders(filtered);
  };


  useEffect(() => {
    const receivedRemindersList = receivedReminders && typeof receivedReminders !== "string" ? receivedReminders : [];
    const createdRemindersList = createdReminders && typeof createdReminders !== "string" ? createdReminders : [];
    let reminders = [...receivedRemindersList, ...createdRemindersList];

      setReminders(reminders);
      setFilteredReminders(applyFilters(searchQuery, reminders, showUnreadOnly));

  }, [receivedReminders, createdReminders]);

  const tabs = [
    {
      label: "Received",
      count: filteredReminders.filter((r) => !r.onlyMine)?.length || 0,
    },
    {
      label: "Created by me",
      count: filteredReminders.filter((r) => r.onlyMine === true)?.length || 0,
    },
    {
      label: "Snoozed",
      count: filteredReminders.filter((r) => !r.onlyMine && r.snoozedUntil !== null)?.length || 0,
    },
  ];

  const panels = {
    Received: (
      <div className={styles.remindersList}>
        {filteredReminders.length > 0 ? (
          filteredReminders
            .filter((r) => !r.onlyMine && r.dateSent !== null)
            .map((reminder, index) => (
              <RemindersCard
                key={index}
                item={reminder}
                setIsModalOpen={setIsModalOpen}
                modalType={setModalType}
                setReminder={setReminder}
                fetchReminders={() => {
                  dispatch(getReminders());
                }}
              />
            ))
        ) : (
          <div className={styles.emptyState}>
            <p className="text-sm-400 grey36Color text-center">
              {searchQuery || showUnreadOnly ? "No reminders found" : "No reminders yet"}
            </p>
          </div>
        )}
      </div>
    ),
    "Created by me": (
      <div className={styles.remindersList}>
        {filteredReminders.length > 0 ? (
          filteredReminders
            .filter((r) => r.onlyMine === true)
            .map((reminder, index) => (
              <SelfCreated
                key={index}
                item={reminder}
                setIsModalOpen={setIsModalOpen}
                modalType={setModalType}
                setReminder={setReminder}
                setIsCreate={setIsCreate}
              />
            ))
        ) : (
          <div className={styles.emptyState}>
            <p className="text-sm-400 grey36Color text-center">
              {searchQuery || showUnreadOnly ? "No reminders found" : "No reminders created yet"}
            </p>
          </div>
        )}
      </div>
    ),
    Snoozed: (
      <div className={styles.remindersList}>
        {filteredReminders.length > 0 ? (
          filteredReminders
            .filter((r) => !r.onlyMine && r.snoozedUntil !== null)
            .map((reminder, index) => (
              <RemindersCard
                key={index}
                item={reminder}
                setIsModalOpen={setIsModalOpen}
                modalType={setModalType}
                setReminder={setReminder}
                fetchReminders={() => {
                  dispatch(getReminders());
                }}
              />
            ))
        ) : (
          <div className={styles.emptyState}>
            <p className="text-sm-400 grey36Color text-center">
              {searchQuery || showUnreadOnly ? "No reminders found" : "No snoozed reminders"}
            </p>
          </div>
        )}
      </div>
    ),
  };

  useEffect(() => {
    dispatch(getReminders());
  }, []);

  return (
    <>
      <div className={`${styles.mainWrapper} font-family-imp`}>
        <h3>Reminders</h3>
        <div className={styles.reminderDetailContainer}>
          <RemindersHeader
            headerBg={styles.headerBg}
            setIsModalOpen={setIsModalOpen}
            modalType={setModalType}
            setIsCreate={setIsCreate}
            handleSearch={handleSearch}
            onFilterChange={handleFilterChange}
            loading={false}
          />
          <RoundedTabs
            tabs={tabs}
            panels={panels}
            panelStyle={styles.reminderPanel}
          />
        </div>
      </div>
      {isModalOpen && (
        <NotificationModals
          setIsModalOpen={setIsModalOpen}
          notification={reminder}
          modalType={modalType}
          isCreate={isCreate}
        />
      )}
    </>
  );
};

export default Reminders;
